import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ConfirmDialog } from './components/ConfirmDialog';
import { DefaultDialog } from './components/DefaultDialog';
import { FormDialog } from './components/FormDialog';
import { ModalDialog } from './components/ModalDialog';
import { StepperDialog } from './components/StepperDialog';
import {
    DialogConfig,
    DialogContextValues,
    DialogType,
    StepperDialogConfig,
} from './types';

import { DialogSize } from './constraints';

export { type DialogPassThroughOptions } from 'primereact/dialog';
export {
    type ConfirmCloseConfig,
    type DialogConfig,
    type StepperDialogConfig,
    type StepperDialogWrapper,
} from './types';
export { CloseConfirmationDialog } from './components/CloseConfirmationDialog';
export {
    useStepperDialog,
    useStepperDialogSteps,
} from './contexts/StepperDialogContext';

const emptyDialogState: Record<string, unknown> = {};

const DialogContext = createContext<DialogContextValues>({
    open: () => null,
    close: () => null,
    confirm: () => null,
    form: () => null,
    modal: () => null,
    stepper: () => null,
    loading: false,
    handleSubmit: () => null,
    setDisabled: () => {},
    disabled: false,
    dialogState: emptyDialogState,
    setDialogState: () => {},
});

export function DialogProvider({ children }: PropsWithChildren) {
    const [dialogConfig, setDialogConfig] = useState<DialogConfig | null>(null);
    const [dialogInstanceKey, setDialogInstanceKey] = useState(0);
    const [loading, setLoading] = useState<boolean>(false);
    const submitRef = useRef<
        ((config: DialogConfig | null) => Promise<void>) | undefined
    >(undefined);
    const [disabled, setDisabled] = useState<boolean>(false);
    const [dialogState, setDialogState] =
        useState<Record<string, unknown>>(emptyDialogState);

    const dialogConfigRef = useRef(dialogConfig);
    dialogConfigRef.current = dialogConfig;

    const onSubmit = useCallback(() => {
        const submit = submitRef.current;
        if (!submit) {
            return;
        }

        setLoading(true);
        // Callers own user-facing errors (toasts); this only keeps a rejected
        // submit from escaping as an unhandled rejection. The dialog stays open.
        submit(dialogConfigRef.current)
            .catch(() => undefined)
            .finally(() => setLoading(false));
    }, []);

    const applyDefaultConfig = useCallback(
        (config: Omit<DialogConfig, 'type'>, type: DialogType): void => {
            setDisabled(false);
            setDialogState(emptyDialogState);
            setDialogInstanceKey((current) => current + 1);
            setDialogConfig({
                ...config,
                type,
            });
        },
        [],
    );

    const openDialog = useCallback(
        (config: Omit<DialogConfig, 'type'>): void => {
            applyDefaultConfig(config, DialogType.Default);
        },
        [applyDefaultConfig],
    );

    const openModal = useCallback(
        (config: Omit<DialogConfig, 'type'>): void => {
            applyDefaultConfig(config, DialogType.Modal);
        },
        [applyDefaultConfig],
    );

    const openForm = useCallback(
        (config: Omit<DialogConfig, 'type'>): void => {
            setLoading(false);
            applyDefaultConfig(config, DialogType.Form);
        },
        [applyDefaultConfig],
    );

    const openConfirm = useCallback(
        (config: Omit<DialogConfig, 'type'>): void => {
            setLoading(false);
            applyDefaultConfig(config, DialogType.Confirm);
        },
        [applyDefaultConfig],
    );

    const openStepper = useCallback(
        <TWrapperProps extends object = object>(
            config: Omit<StepperDialogConfig<TWrapperProps>, 'type'>,
        ): void => {
            setLoading(false);
            setDisabled(false);
            setDialogState(emptyDialogState);
            setDialogInstanceKey((current) => current + 1);
            setDialogConfig({
                ...config,
                type: DialogType.Stepper,
            } as DialogConfig);
        },
        [],
    );

    const closeDialog = useCallback((): void => {
        setLoading(false);
        submitRef.current = undefined;
        setDialogConfig(null);
        setDialogState(emptyDialogState);
    }, []);

    const handleSubmit = useCallback(
        (callback: (config?: DialogConfig | null) => Promise<void>): void => {
            submitRef.current = callback;
        },
        [],
    );

    const dialog = useMemo(() => {
        if (!dialogConfig) {
            return null;
        }
        const {
            content,
            title,
            subtitle,
            type,
            size,
            buttons,
            dialogClassName,
        } = dialogConfig;
        switch (type) {
            case DialogType.Confirm:
                return (
                    <ConfirmDialog
                        key={dialogInstanceKey}
                        title={title}
                        buttons={buttons}
                        onClose={closeDialog}
                        loading={loading}
                        onSubmit={onSubmit}
                    >
                        {content}
                    </ConfirmDialog>
                );
            case DialogType.Form:
                return (
                    <FormDialog
                        key={dialogInstanceKey}
                        title={title}
                        subtitle={subtitle}
                        buttons={buttons}
                        onClose={closeDialog}
                        size={size ?? DialogSize.md}
                        disabled={disabled}
                        loading={loading}
                        onSubmit={onSubmit}
                        pt={dialogConfig.pt}
                        confirmClose={dialogConfig.confirmClose}
                        dialogClassName={dialogClassName}
                    >
                        {content}
                    </FormDialog>
                );
            case DialogType.Modal:
                return (
                    <ModalDialog
                        key={dialogInstanceKey}
                        dialogClassName={dialogClassName}
                        title={title}
                        buttons={buttons}
                        size={size}
                        onClose={closeDialog}
                        pt={dialogConfig.pt}
                    >
                        {content}
                    </ModalDialog>
                );
            case DialogType.Default:
                return (
                    <DefaultDialog
                        key={dialogInstanceKey}
                        title={title}
                        buttons={buttons}
                        onClose={closeDialog}
                    >
                        {content}
                    </DefaultDialog>
                );
            case DialogType.Stepper: {
                const stepperConfig = dialogConfig as StepperDialogConfig;
                return (
                    <StepperDialog
                        key={dialogInstanceKey}
                        steps={stepperConfig.steps}
                        title={title}
                        subtitle={subtitle}
                        allStepsReached={stepperConfig.allStepsReached}
                        linear={stepperConfig.linear}
                        onClose={closeDialog}
                        size={size}
                        loading={loading}
                        setLoading={setLoading}
                        pt={dialogConfig.pt}
                        buttons={buttons}
                        dialogClassName={dialogClassName}
                        wrapper={stepperConfig.wrapper}
                        confirmClose={stepperConfig.confirmClose}
                    />
                );
            }
            default:
                return null;
        }
    }, [
        dialogConfig,
        dialogInstanceKey,
        disabled,
        loading,
        onSubmit,
        closeDialog,
    ]);

    const contextValue = useMemo<DialogContextValues>(
        () => ({
            open: openDialog,
            close: closeDialog,
            form: openForm,
            confirm: openConfirm,
            modal: openModal,
            stepper: openStepper,
            loading,
            handleSubmit,
            setDisabled,
            disabled,
            onSubmit,
            dialogState,
            setDialogState,
        }),
        [
            openDialog,
            closeDialog,
            openForm,
            openConfirm,
            openModal,
            openStepper,
            loading,
            handleSubmit,
            disabled,
            onSubmit,
            dialogState,
        ],
    );

    return (
        <DialogContext.Provider value={contextValue}>
            {children}
            {dialog}
        </DialogContext.Provider>
    );
}

export function useDialog<
    TState extends Record<string, unknown> = Record<string, unknown>,
>(): DialogContextValues<TState> {
    return useContext(DialogContext) as DialogContextValues<TState>;
}
